// /welcome — the guided first-login welcome.
//
// A short explanation of how Goblic works, then (for workspace owners and
// administrators) what a first run needs, in the order the app itself is
// built: an executor login, GitHub and roles for the workspace; then a
// project, its workflows and the boards (streams) they bring; then a first
// agent. It ends with a map of where every setting lives. Every setup step
// can be skipped, and steps that are already in place show as done.
// Finishing or skipping marks the welcome as seen (PUT /me/onboarding);
// Settings can open it again.
//
// The step and the project being set up live in the URL (?step=&project=),
// so the GitHub round trip can come back to the right place.

import {
  useEffect,
  useMemo,
  useState,
  type FormEvent,
  type ReactNode,
} from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link, useNavigate, useSearch } from '@tanstack/react-router'
import {
  DEFAULT_ROLE_RUN_SETTINGS,
  PLANNER_RUN_SETTINGS,
  type Project,
  type StreamDefinition,
} from '@goblic/shared'
import {
  ApiError,
  agentsApi,
  connectionsApi,
  CONNECTION_KIND_LABEL,
  type ExecutorConnection,
  githubApi,
  meApi,
  projectsApi,
  rolesApi,
  streamsApi,
  workflowsApi,
} from '../lib/api-client'
import { useAuth, useWorkspaceId } from '../lib/auth'
import { useIllustrations } from '../lib/illustrations'
import { LogoMark } from '../components/logo'
import {
  ClayIcon,
  RoleAvatar,
  type ClayIconName,
} from '../components/goblin-art'
import { STREAM_COLOR_CLASS } from '../components/board/board-utils'
import { ConnectForm, GitHubTokenForm } from '../components/github-connect'
import { GITHUB_RETURN_KEY } from './connect-github'
import { ConnectionForm } from './executors'
import { ModelPicker } from '../components/model-picker'
import { Field, Notice } from '../components/ui'
import {
  CreateProjectForm,
  PROJECT_MANAGER_ROLES,
  useCreateProjectForm,
} from '../components/projects/create-project-form'

export const WELCOME_STEPS = [
  'hello',
  'how',
  'executor',
  'github',
  'roles',
  'project',
  'workflows',
  'boards',
  'agent',
  'tour',
  'done',
] as const

export type WelcomeStep = (typeof WELCOME_STEPS)[number]

export interface WelcomeSearch {
  step?: WelcomeStep
  /** Key of the project the project-level steps work on; else the newest. */
  project?: string
}

const STEP_LABEL: Record<WelcomeStep, string> = {
  hello: 'Welcome',
  how: 'How it works',
  executor: 'Executor',
  github: 'GitHub',
  roles: 'Roles',
  project: 'Project',
  workflows: 'Workflows',
  boards: 'Boards',
  agent: 'First agent',
  tour: 'Where things live',
  done: 'Ready',
}

const SETUP_STEPS: WelcomeStep[] = [
  'executor',
  'github',
  'roles',
  'project',
  'workflows',
  'boards',
  'agent',
]
const MANAGER_ROLES = PROJECT_MANAGER_ROLES

function errorText(error: unknown, fallback: string): string {
  return error instanceof ApiError ? error.message : fallback
}

/** The list comes in id order; the newest is the one created last. */
function newestProject(projects: Project[]): Project | null {
  return projects.reduce<Project | null>(
    (newest, p) => (!newest || p.createdAt > newest.createdAt ? p : newest),
    null
  )
}

export function WelcomePage() {
  const search = useSearch({ strict: false }) as WelcomeSearch
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { user } = useAuth()
  const workspaceId = useWorkspaceId()
  const [illustrations] = useIllustrations()
  const isManager = (user?.roles ?? []).some((role) =>
    MANAGER_ROLES.includes(role)
  )

  const steps: WelcomeStep[] = isManager
    ? ['hello', 'how', ...SETUP_STEPS, 'tour', 'done']
    : ['hello', 'how', 'tour', 'done']
  const step: WelcomeStep =
    search.step && steps.includes(search.step) ? search.step : 'hello'
  const index = steps.indexOf(step)

  const meQuery = useQuery({ queryKey: ['me'], queryFn: meApi.get })
  const enabled = workspaceId !== '' && isManager
  const connectionsQuery = useQuery({
    queryKey: ['connections', workspaceId],
    queryFn: () => connectionsApi.list(workspaceId),
    enabled,
  })
  const githubQuery = useQuery({
    queryKey: ['github', workspaceId],
    queryFn: () => githubApi.status(workspaceId),
    enabled,
  })
  // Same key as the shell's project context, so a project made here shows
  // up in the sidebar's switcher right away.
  const projectsQuery = useQuery({
    queryKey: ['projects', workspaceId, 'all'],
    queryFn: () => projectsApi.listAll({ workspaceId }),
    enabled: workspaceId !== '',
  })
  const agentsQuery = useQuery({
    queryKey: ['agents', workspaceId],
    queryFn: () => agentsApi.list({ workspaceId }),
    enabled,
  })

  const connections = connectionsQuery.data?.items ?? []
  const github = githubQuery.data
  const projects = projectsQuery.data ?? []
  const agents = agentsQuery.data?.items ?? []

  // The project the project-level steps work on: the one in the URL, else
  // the newest.
  const project =
    projects.find((p) => p.key === search.project) ?? newestProject(projects)

  const streamsQuery = useQuery({
    queryKey: ['project', project?.id ?? '', 'streams'],
    queryFn: () => streamsApi.list(project!.id),
    enabled: project != null,
  })
  const streams = streamsQuery.data?.items ?? []

  const rolesQuery = useQuery({
    queryKey: ['roles', workspaceId],
    queryFn: () => rolesApi.list({ workspaceId }),
    enabled,
  })
  const existingRoles = rolesQuery.data?.items ?? []

  const done: Record<WelcomeStep, boolean> = {
    hello: index > 0,
    how: index > 1,
    executor: connections.length > 0,
    github:
      github?.apps.some((a) => a.installations.length > 0) || github?.token != null,
    roles: existingRoles.length > 0,
    project: projects.length > 0,
    workflows: streams.length > 0,
    boards: streams.length > 0,
    agent: agents.length > 0,
    tour: index > steps.indexOf('tour'),
    done: false,
  }

  const finish = useMutation({
    mutationFn: () => meApi.setOnboarding(true),
    onSuccess: (me) => queryClient.setQueryData(['me'], me),
  })

  function go(next: WelcomeStep, projectKey = search.project): void {
    void navigate({
      to: '/welcome',
      search: { step: next, ...(projectKey ? { project: projectKey } : {}) },
    })
  }
  const next = () => go(steps[Math.min(index + 1, steps.length - 1)]!)
  const back = () => go(steps[Math.max(index - 1, 0)]!)
  const refresh = (...key: string[]) =>
    queryClient.invalidateQueries({ queryKey: key })

  // Reaching the last step counts as finishing the welcome.
  useEffect(() => {
    if (step === 'done' && !meQuery.data?.onboardedAt && !finish.isPending) {
      finish.mutate()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, meQuery.data?.onboardedAt])

  async function skipAll(): Promise<void> {
    await finish.mutateAsync().catch(() => undefined)
    // `/` opens the last project's overview, or All projects without one.
    await navigate({ to: '/' })
  }

  // Greet by first name, unless the "name" is really a role label.
  const displayName = meQuery.data?.displayName ?? ''
  const name = /owner|admin|operator|approver|viewer/i.test(displayName)
    ? undefined
    : displayName.split(' ')[0] || undefined

  return (
    <div className="min-h-screen bg-canvas text-ink">
      <header className="mx-auto flex max-w-5xl items-center justify-between gap-4 px-4 py-5 sm:px-6">
        <span className="flex items-center gap-2.5">
          <LogoMark size={30} />
          <span className="font-display text-lg font-semibold">Goblic</span>
        </span>
        {step !== 'done' ? (
          <button
            type="button"
            className="btn-ghost btn-sm"
            onClick={() => void skipAll()}
          >
            Skip the welcome
          </button>
        ) : null}
      </header>

      <main className="mx-auto grid max-w-5xl gap-6 px-4 pb-16 sm:px-6 lg:grid-cols-[210px_minmax(0,1fr)] lg:gap-10">
        <nav aria-label="Welcome steps" className="lg:pt-2">
          <p className="mb-3 text-sm text-ink-3 lg:hidden">
            Step {index + 1} of {steps.length}: {STEP_LABEL[step]}
          </p>
          <ol className="hidden space-y-1 lg:block">
            {steps.map((s, i) => {
              const current = s === step
              const complete = done[s] && !current
              return (
                <li key={s}>
                  <button
                    type="button"
                    onClick={() => go(s)}
                    aria-current={current ? 'step' : undefined}
                    className={`flex w-full items-center gap-3 rounded-control px-2.5 py-2 text-left text-base transition-colors ${
                      current
                        ? 'bg-surface font-medium text-ink shadow-sm'
                        : 'text-ink-2 hover:bg-surface/60 hover:text-ink'
                    }`}
                  >
                    <span
                      className={`grid h-6 w-6 shrink-0 place-items-center rounded-full text-xs font-semibold ${
                        complete
                          ? 'bg-ok text-surface'
                          : current
                            ? 'bg-action text-on-action'
                            : 'bg-sunken text-ink-3'
                      }`}
                      aria-hidden
                    >
                      {complete ? '✓' : i + 1}
                    </span>
                    {STEP_LABEL[s]}
                  </button>
                </li>
              )
            })}
          </ol>
        </nav>

        <section className="panel p-6 sm:p-8">
          {step === 'hello' ? (
            <Hello
              name={name}
              isManager={isManager}
              illustrations={illustrations}
              onNext={next}
            />
          ) : step === 'how' ? (
            <HowItWorks isManager={isManager} onNext={next} onBack={back} />
          ) : step === 'executor' ? (
            <ExecutorStep
              workspaceId={workspaceId}
              connections={connections}
              onSaved={() => void refresh('connections', workspaceId)}
              onNext={next}
              onBack={back}
            />
          ) : step === 'github' ? (
            <GitHubStep
              workspaceId={workspaceId}
              connected={done.github}
              account={github?.app?.installations[0]?.account}
              permissions={github?.permissions ?? {}}
              onSaved={() => void refresh('github', workspaceId)}
              onNext={next}
              onBack={back}
            />
          ) : step === 'roles' ? (
            <RolesStep
              workspaceId={workspaceId}
              existingRoles={existingRoles.map((r) => r.name)}
              onSaved={() => void refresh('roles', workspaceId)}
              onNext={next}
              onBack={back}
            />
          ) : step === 'project' ? (
            <ProjectStep
              workspaceId={workspaceId}
              projects={projects}
              current={project}
              githubConnected={done.github}
              onPick={(key) => go('project', key)}
              onCreated={(created) => {
                void refresh('projects')
                go('workflows', created.key)
              }}
              onNext={next}
              onSkip={() => go('agent')}
              onBack={back}
            />
          ) : step === 'workflows' ? (
            <WorkflowsStep
              workspaceId={workspaceId}
              project={project}
              onSaved={() => {
                if (project) void refresh('project', project.id)
              }}
              onNext={next}
              onBack={back}
            />
          ) : step === 'boards' ? (
            <BoardsStep
              project={project}
              streams={streams}
              loading={streamsQuery.isLoading}
              onNext={next}
              onBack={back}
            />
          ) : step === 'agent' ? (
            <AgentStep
              workspaceId={workspaceId}
              agents={agents.map((a) => a.name)}
              roles={existingRoles}
              projects={projects.map((p) => ({ id: p.id, name: p.name }))}
              defaultProjectId={project?.id ?? ''}
              connections={connections}
              onSaved={() => void refresh('agents', workspaceId)}
              onNext={next}
              onBack={back}
            />
          ) : step === 'tour' ? (
            <WhereThingsLive isManager={isManager} onNext={next} onBack={back} />
          ) : (
            <Done
              isManager={isManager}
              ready={done.executor && done.workflows && done.agent}
              project={project}
              streams={streams}
              illustrations={illustrations}
            />
          )}
        </section>
      </main>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Steps
// ---------------------------------------------------------------------------

function StepHeader({
  title,
  children,
}: {
  title: string
  children?: ReactNode
}) {
  return (
    <div className="mb-6">
      <h1 className="font-display text-2xl font-semibold tracking-[-0.01em] text-ink [text-wrap:balance]">
        {title}
      </h1>
      {children ? (
        <div className="mt-2 max-w-[62ch] space-y-2 text-base text-ink-2">
          {children}
        </div>
      ) : null}
    </div>
  )
}

function StepActions({
  onBack,
  children,
}: {
  onBack?: () => void
  children: ReactNode
}) {
  return (
    <div className="mt-8 flex flex-wrap items-center justify-between gap-3 border-t border-line pt-5">
      {onBack ? (
        <button type="button" className="btn-ghost btn-sm" onClick={onBack}>
          Back
        </button>
      ) : (
        <span />
      )}
      <div className="flex flex-wrap items-center gap-2">{children}</div>
    </div>
  )
}

function DoneNote({ children }: { children: ReactNode }) {
  return (
    <div className="flex items-center gap-3 rounded-control border border-ok/30 bg-ok-soft px-4 py-3 text-base text-ink">
      <ClayIcon name="checks" size={28} />
      <span>{children}</span>
    </div>
  )
}

function Hello({
  name,
  isManager,
  illustrations,
  onNext,
}: {
  name: string | undefined
  isManager: boolean
  illustrations: boolean
  onNext: () => void
}) {
  return (
    <div className="flex flex-col-reverse items-start gap-6 sm:flex-row sm:items-center">
      <div className="min-w-0 flex-1">
        <StepHeader
          title={name ? `Welcome to Goblic, ${name}` : 'Welcome to Goblic'}
        >
          <p>
            Goblic puts AI agents to work on your code. We call them goblins.
            Every project has boards, one per kind of work such as features or
            bugs. A goblin pulls the next task from a board, works on it in its
            own sealed sandbox, and hands back a draft pull request. Nothing
            leaves the sandbox without your say.
          </p>
          <p>
            {isManager
              ? 'This takes about ten minutes: a quick look at how it works, then setting up what the goblins need for their first job, and a map of where every setting lives.'
              : "A quick look at how it works and where things are, and you're on your way."}
          </p>
        </StepHeader>
        <button type="button" className="btn-primary" onClick={onNext}>
          Show me how it works
        </button>
      </div>
      {illustrations ? (
        <img
          src="/goblins/welcome.webp"
          alt=""
          aria-hidden
          width={200}
          height={200}
          className="h-40 w-40 shrink-0 select-none object-contain sm:h-52 sm:w-52"
        />
      ) : null}
    </div>
  )
}

const FLOW: {
  icon: ClayIconName | 'agent'
  title: string
  text: string
}[] = [
  {
    icon: 'board',
    title: 'A task lands on a board',
    text: "In the Inbox of a stream, like Features or Bugs. Add it yourself, or it comes in from Plane.",
  },
  {
    icon: 'stream',
    title: 'It waits its turn',
    text: 'You sort it into Ready. When there is room, the next task starts by itself, or you press Start next.',
  },
  {
    icon: 'agent',
    title: 'A goblin works on it',
    text: "Step by step along the workflow, each in its own sandbox with only the websites it's allowed.",
  },
  {
    icon: 'bell',
    title: 'It asks before risky steps',
    text: 'Questions and approvals wait for you on your Desk, from anywhere.',
  },
  {
    icon: 'pull-request',
    title: 'You get a draft pull request',
    text: 'With the changes, test results and a report to review.',
  },
]

const WORDS: { term: string; meaning: string }[] = [
  {
    term: 'Project',
    meaning:
      'One codebase with a short key (like GOB), its repository and its rules. Everything else hangs off it.',
  },
  {
    term: 'Stream and board',
    meaning:
      'A kind of work in a project, like Features or Bugs, with its own board: queue lanes on the left, workflow steps on the right.',
  },
  {
    term: 'Workflow',
    meaning:
      'The path a task follows: which role does what, who reviews, and where you decide. Every workflow gets its own stream.',
  },
  {
    term: 'Role',
    meaning:
      'A job description, like Developer or Reviewer: instructions, tools and limits.',
  },
  {
    term: 'Agent',
    meaning:
      'A role put to work: role + executor + model, for one project or all of them.',
  },
  {
    term: 'Executor',
    meaning: 'The login an agent thinks with: Claude Code, a ChatGPT subscription or an API.',
  },
  {
    term: 'Run',
    meaning:
      'One goblin working one step of a task, live on screen from start to pull request.',
  },
  {
    term: 'Desk',
    meaning: 'Everything waiting on you: approvals, questions and human steps.',
  },
]

function HowItWorks({
  isManager,
  onNext,
  onBack,
}: {
  isManager: boolean
  onNext: () => void
  onBack: () => void
}) {
  return (
    <div>
      <StepHeader title="How it works">
        <p>Every piece of work follows the same path.</p>
      </StepHeader>
      <ol className="grid gap-3 sm:grid-cols-2 xl:grid-cols-5">
        {FLOW.map((item, i) => (
          <li
            key={item.title}
            className="flex gap-3 rounded-control border border-line bg-sunken/40 p-3 xl:flex-col"
          >
            <span className="flex shrink-0 items-center gap-2">
              {item.icon === 'agent' ? (
                <RoleAvatar roleName="Developer" size={32} />
              ) : (
                <ClayIcon
                  name={item.icon}
                  size={32}
                  fallback={
                    <span className="grid h-8 w-8 place-items-center rounded-full bg-moss-soft text-sm font-semibold text-moss">
                      {i + 1}
                    </span>
                  }
                />
              )}
            </span>
            <span className="min-w-0">
              <span className="block text-base font-medium leading-snug text-ink">
                {item.title}
              </span>
              <span className="mt-0.5 block text-sm text-ink-3">
                {item.text}
              </span>
            </span>
          </li>
        ))}
      </ol>

      <h2 className="mb-3 mt-8 font-display text-md font-semibold text-ink">
        Words you'll see
      </h2>
      <dl className="grid gap-x-8 gap-y-3 sm:grid-cols-2">
        {WORDS.map((word) => (
          <div key={word.term}>
            <dt className="text-base font-medium text-ink">{word.term}</dt>
            <dd className="text-sm text-ink-3">{word.meaning}</dd>
          </div>
        ))}
      </dl>

      <StepActions onBack={onBack}>
        <button type="button" className="btn-primary" onClick={onNext}>
          {isManager ? 'Set it up' : 'Got it'}
        </button>
      </StepActions>
    </div>
  )
}

function ExecutorStep({
  workspaceId,
  connections,
  onSaved,
  onNext,
  onBack,
}: {
  workspaceId: string
  connections: ExecutorConnection[]
  onSaved: () => void
  onNext: () => void
  onBack: () => void
}) {
  const [adding, setAdding] = useState(connections.length === 0)

  return (
    <div>
      <StepHeader title="Give the goblins a brain">
        <p>
          Agents think with a coding tool: Claude Code, a ChatGPT
          subscription, or any OpenAI-compatible API. Goblic keeps the login
          encrypted and only hands it to a sandbox while a run is working.
          You can add more later, such as a second account.
        </p>
      </StepHeader>

      {connections.length && !adding ? (
        <div className="space-y-3">
          <DoneNote>
            {connections.length === 1
              ? `${connections[0]!.name} is connected.`
              : `${connections.length} connections: ${connections.map((c) => c.name).join(', ')}.`}
          </DoneNote>
          <button
            type="button"
            className="btn-ghost btn-sm"
            onClick={() => setAdding(true)}
          >
            Add another
          </button>
        </div>
      ) : (
        <ConnectionForm
          workspaceId={workspaceId}
          existing={null}
          onDone={() => {
            setAdding(false)
            onSaved()
          }}
        />
      )}

      <StepActions onBack={onBack}>
        {connections.length ? (
          <button type="button" className="btn-primary" onClick={onNext}>
            Continue
          </button>
        ) : (
          <button type="button" className="btn-ghost" onClick={onNext}>
            Skip for now
          </button>
        )}
      </StepActions>
    </div>
  )
}

function GitHubStep({
  workspaceId,
  connected,
  account,
  permissions,
  onSaved,
  onNext,
  onBack,
}: {
  workspaceId: string
  connected: boolean
  account: string | undefined
  permissions: Record<string, string>
  onSaved: () => void
  onNext: () => void
  onBack: () => void
}) {
  const [useToken, setUseToken] = useState(false)

  function rememberReturn(): void {
    try {
      sessionStorage.setItem(GITHUB_RETURN_KEY, '/welcome?step=github')
    } catch {
      // without storage the round trip ends on Credentials instead
    }
  }

  return (
    <div>
      <StepHeader title="Connect GitHub">
        <p>
          So finished work can become a draft pull request. Goblic creates its
          own GitHub App; you choose the repositories, and each run gets a key
          for just its own repository.
        </p>
      </StepHeader>

      {connected ? (
        <DoneNote>
          GitHub is connected{account ? ` for ${account}` : ''}.
        </DoneNote>
      ) : useToken ? (
        <GitHubTokenForm workspaceId={workspaceId} onDone={onSaved} />
      ) : (
        <ConnectForm
          workspaceId={workspaceId}
          permissions={permissions}
          onUseToken={() => setUseToken(true)}
          onBeforeLeave={rememberReturn}
        />
      )}

      <StepActions onBack={onBack}>
        {connected ? (
          <button type="button" className="btn-primary" onClick={onNext}>
            Continue
          </button>
        ) : (
          <button type="button" className="btn-ghost" onClick={onNext}>
            Skip for now
          </button>
        )}
      </StepActions>
      {!connected ? (
        <p className="mt-3 text-sm text-ink-3">
          Without GitHub, runs still work: they keep their changes as a diff on
          the run.
        </p>
      ) : null}
    </div>
  )
}

const DEFAULT_ROLE_TEMPLATES: {
  name: string
  description: string
  baseInstructions: string
  workload: 'light' | 'heavy'
}[] = [
  {
    name: 'Researcher',
    workload: 'light',
    description: 'Gathers and structures research and background information for a task.',
    baseInstructions:
      'You are the Researcher role. Thoroughly research the given question, cite sources, and deliver ' +
      'a structured summary without modifying any code yourself.',
  },
  {
    name: 'Product Owner',
    workload: 'light',
    description: 'Guards scope, priority and acceptance criteria from a product perspective.',
    baseInstructions:
      'You are the Product Owner role. Clarify scope and acceptance criteria, guard priority, and ' +
      'do not propose implementation details that fall outside the product question.',
  },
  {
    name: 'Spec Writer',
    workload: 'light',
    description: 'Writes a functional or technical specification based on a task.',
    baseInstructions:
      'You are the Spec Writer role. Write a clear, testable specification (goal, scope, ' +
      'acceptance criteria, open questions) based on the provided task.',
  },
  {
    name: 'Planner',
    workload: 'light',
    description: 'Breaks a task into actionable steps and identifies dependencies.',
    baseInstructions:
      'You are the Planner role. Break the task into small, actionable steps, identify dependencies ' +
      'and risks, and do not deliver production code.',
  },
  {
    name: 'Developer',
    workload: 'heavy',
    description: 'Implements changes in a temporary development sandbox.',
    baseInstructions:
      'You are the Developer role. Implement the requested change according to the project conventions, ' +
      'write tests, and respect the project policy and forbidden actions at all times.',
  },
  {
    name: 'Tester',
    workload: 'heavy',
    description: 'Runs tests and quality checks on a change.',
    baseInstructions:
      'You are the Tester role. Run the test suite and relevant quality checks, report failed tests ' +
      'with sufficient context, and do not modify production code yourself.',
  },
  {
    name: 'Reviewer',
    workload: 'light',
    description: 'Reviews a diff with fresh context, without the developer run history.',
    baseInstructions:
      'You are the Reviewer role. Review the diff for correctness, readability and project conventions ' +
      'with fresh context — do not use the full conversation history of the developer run.',
  },
  {
    name: 'Security Reviewer',
    workload: 'light',
    description: 'Reviews a change specifically for security risks.',
    baseInstructions:
      'You are the Security Reviewer role. Review the diff specifically for security risks ' +
      '(secrets, injection, permissions, dependencies) and report findings with risk level.',
  },
]

function RolesStep({
  workspaceId,
  existingRoles,
  onSaved,
  onNext,
  onBack,
}: {
  workspaceId: string
  existingRoles: string[]
  onSaved: () => void
  onNext: () => void
  onBack: () => void
}) {
  const [selected, setSelected] = useState<Set<string>>(
    () => new Set(DEFAULT_ROLE_TEMPLATES.map((r) => r.name))
  )
  const [creating, setCreating] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const queryClient = useQueryClient()

  function toggle(name: string): void {
    setSelected((prev) => {
      const next = new Set(prev)
      if (next.has(name)) next.delete(name)
      else next.add(name)
      return next
    })
  }

  async function handleCreate(): Promise<void> {
    const toCreate = DEFAULT_ROLE_TEMPLATES.filter(
      (r) => selected.has(r.name) && !existingRoles.includes(r.name)
    )
    if (toCreate.length === 0) { onNext(); return }
    setCreating(true)
    setError(null)
    try {
      for (const template of toCreate) {
        await rolesApi.create({
          workspaceId,
          name: template.name,
          description: template.description,
          baseInstructions: template.baseInstructions,
          workload: template.workload,
          skillIds: [],
          allowedMcpServiceIds: [],
          approvalRequiredActions: [],
          runSettings: template.name === 'Planner' ? PLANNER_RUN_SETTINGS : DEFAULT_ROLE_RUN_SETTINGS,
          allowedRiskLevel: 'high',
        })
      }
      await queryClient.invalidateQueries({ queryKey: ['roles', workspaceId] })
      onSaved()
      onNext()
    } catch {
      setError("Couldn't create the roles. Try again.")
    } finally {
      setCreating(false)
    }
  }

  if (existingRoles.length > 0) {
    return (
      <div>
        <StepHeader title="Roles">
          <p>Roles define what a goblin does: its instructions, tools and limits.</p>
        </StepHeader>
        <DoneNote>{existingRoles.length} role{existingRoles.length !== 1 ? 's' : ''} already set up: {existingRoles.join(', ')}.</DoneNote>
        <StepActions onBack={onBack}>
          <button type="button" className="btn-primary" onClick={onNext}>Continue</button>
        </StepActions>
      </div>
    )
  }

  return (
    <div>
      <StepHeader title="Choose your roles">
        <p>
          Roles define what a goblin does: its instructions, tools and limits. Pick
          the ones you want to start with. You can edit them or add more later.
        </p>
      </StepHeader>

      <ul className="space-y-2">
        {DEFAULT_ROLE_TEMPLATES.map((template) => (
          <li key={template.name}>
            <label className="flex cursor-pointer items-start gap-3 rounded-control border border-line bg-sunken/30 px-4 py-3 hover:bg-surface has-[:checked]:border-action/40 has-[:checked]:bg-action-soft">
              <input
                type="checkbox"
                className="mt-0.5 shrink-0 accent-action"
                checked={selected.has(template.name)}
                onChange={() => toggle(template.name)}
              />
              <span className="min-w-0">
                <span className="block text-base font-medium text-ink">{template.name}</span>
                <span className="block text-sm text-ink-3">{template.description}</span>
              </span>
              <span className={`ml-auto shrink-0 self-center rounded-full px-2 py-0.5 text-xs font-medium ${template.workload === 'heavy' ? 'bg-warning-soft text-warning' : 'bg-info-soft text-info'}`}>
                {template.workload}
              </span>
            </label>
          </li>
        ))}
      </ul>

      {error ? <Notice tone="error">{error}</Notice> : null}

      <StepActions onBack={onBack}>
        <button type="button" className="btn-ghost" onClick={onNext}>
          Skip for now
        </button>
        <button
          type="button"
          className="btn-primary"
          disabled={creating || selected.size === 0}
          onClick={() => void handleCreate()}
        >
          {creating ? 'Creating...' : `Create ${selected.size} role${selected.size !== 1 ? 's' : ''}`}
        </button>
      </StepActions>
    </div>
  )
}

function ProjectStep({
  workspaceId,
  projects,
  current,
  githubConnected,
  onPick,
  onCreated,
  onNext,
  onSkip,
  onBack,
}: {
  workspaceId: string
  projects: Project[]
  current: Project | null
  githubConnected: boolean
  onPick: (key: string) => void
  onCreated: (project: Project) => void
  onNext: () => void
  /** Skipped without any project: workflows and boards need one, so jump past them. */
  onSkip: () => void
  onBack: () => void
}) {
  const [adding, setAdding] = useState(projects.length === 0)
  const controller = useCreateProjectForm({
    workspaceId,
    projects,
    githubConnected,
    onCreated: (project) => {
      setAdding(false)
      onCreated(project)
    },
  })

  return (
    <div>
      <StepHeader title="Create a project">
        <p>
          A project is one codebase: its repository, its rules and its boards.
          The short key (like GOB) names its tickets, GOB-12, and sits in every
          link to it. You can add or change the repository later in Project
          settings.
        </p>
      </StepHeader>

      {projects.length && !adding ? (
        <div className="space-y-3">
          <DoneNote>
            {projects.length === 1
              ? `You have a project: ${projects[0]!.name} (${projects[0]!.key}).`
              : `You have ${projects.length} projects.`}
          </DoneNote>
          {projects.length > 1 ? (
            <Field
              label="Set up"
              htmlFor="welcome-project-pick"
              hint="The next steps (workflows, boards, agent) work on this project."
            >
              <select
                id="welcome-project-pick"
                className="field-input max-w-sm"
                value={current?.key ?? ''}
                onChange={(event) => onPick(event.target.value)}
              >
                {projects.map((p) => (
                  <option key={p.id} value={p.key}>
                    {p.name} ({p.key})
                  </option>
                ))}
              </select>
            </Field>
          ) : null}
          <button
            type="button"
            className="btn-ghost btn-sm"
            onClick={() => setAdding(true)}
          >
            Add another
          </button>
        </div>
      ) : (
        <CreateProjectForm controller={controller} formId="project-form" idPrefix="welcome" />
      )}

      <StepActions onBack={onBack}>
        {adding ? (
          <>
            <button
              type="button"
              className="btn-ghost"
              onClick={projects.length ? () => setAdding(false) : onSkip}
            >
              {projects.length ? 'Cancel' : 'Skip for now'}
            </button>
            <button
              type="submit"
              form="project-form"
              className="btn-primary"
              disabled={!controller.canSubmit}
            >
              {controller.pending ? 'Creating…' : 'Create project'}
            </button>
          </>
        ) : (
          <button type="button" className="btn-primary" onClick={onNext}>
            Continue
          </button>
        )}
      </StepActions>
    </div>
  )
}

/** Shown by the project-level steps while there is no project yet. */
function NeedsProject({
  onBack,
  onNext,
}: {
  onBack: () => void
  onNext: () => void
}) {
  return (
    <>
      <Notice tone="signal">
        This step belongs to a project. Go back to create one first, or skip
        it for now: All projects keeps a checklist of what's left.
      </Notice>
      <StepActions onBack={onBack}>
        <button type="button" className="btn-ghost" onClick={onNext}>
          Skip for now
        </button>
      </StepActions>
    </>
  )
}

// Feature and Bug cover the common case (a spec/plan phase plus a
// reproduce-then-fix phase); the rest stay off by default since not every
// project runs hotfixes, chores or spikes through the agent flow.
const DEFAULT_SELECTED_TEMPLATE_KEYS = ['feature', 'bug']

function WorkflowsStep({
  workspaceId,
  project,
  onSaved,
  onNext,
  onBack,
}: {
  workspaceId: string
  project: Project | null
  onSaved: () => void
  onNext: () => void
  onBack: () => void
}) {
  const workflowsQuery = useQuery({
    queryKey: ['project', project?.id ?? '', 'workflows'],
    queryFn: () => workflowsApi.get(project!.id),
    enabled: project != null,
  })
  const templatesQuery = useQuery({
    queryKey: ['workflow-templates', workspaceId],
    queryFn: () => workflowsApi.templates(workspaceId),
    enabled: workspaceId !== '',
  })
  const workflows = workflowsQuery.data?.workflows ?? []
  const defaultWorkflow = workflowsQuery.data?.defaultWorkflow ?? null
  // Templates the project doesn't use yet.
  const templates = (templatesQuery.data ?? []).filter(
    (t) => !workflows.some((w) => w.templateKey === t.key || w.key === t.key)
  )
  const [adding, setAdding] = useState(false)
  const [selected, setSelected] = useState<Set<string>>(
    () => new Set(DEFAULT_SELECTED_TEMPLATE_KEYS)
  )
  const picked = templates.filter((t) => selected.has(t.key))

  function toggle(key: string): void {
    setSelected((prev) => {
      const next = new Set(prev)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      return next
    })
  }

  const apply = useMutation({
    mutationFn: async () => {
      // One at a time and in the order shown: the first one applied becomes
      // the project's default workflow (fromTemplate leaves an existing
      // default alone). Each one gets its own stream and board.
      for (const template of picked) {
        await workflowsApi.fromTemplate(project!.id, {
          templateKey: template.key,
          createMissingRoles: true,
        })
      }
    },
    onSuccess: () => {
      setAdding(false)
      onSaved()
      onNext()
    },
  })

  if (!project) {
    return (
      <div>
        <StepHeader title="Choose workflows" />
        <NeedsProject onBack={onBack} onNext={onNext} />
      </div>
    )
  }

  const showPicker = workflows.length === 0 || adding

  return (
    <div>
      <StepHeader title={`Workflows for ${project.name}`}>
        <p>
          A workflow is the path a task follows: which role does what, who
          reviews, and where you decide. Every workflow gets its own stream:
          a board with its own queue and ticket prefix, like Features or Bugs.
        </p>
        <p>
          Pick the kinds of work this project has. Each starts from a
          ready-made flow you can redraw later in the workflow editor; roles
          it needs that you don't have yet are created for you.
        </p>
      </StepHeader>

      {workflowsQuery.isLoading || templatesQuery.isLoading ? (
        <p className="text-sm text-ink-3">Loading…</p>
      ) : templatesQuery.isError ? (
        <Notice tone="error">Couldn't load the workflow templates.</Notice>
      ) : (
        <div className="space-y-4">
          {workflows.length ? (
            <DoneNote>
              {workflows.length === 1 ? 'One workflow: ' : `${workflows.length} workflows: `}
              {workflows
                .map((w) =>
                  w.key === defaultWorkflow ? `${w.name || w.key} (default)` : w.name || w.key
                )
                .join(', ')}
              .
            </DoneNote>
          ) : null}
          {showPicker ? (
            templates.length ? (
              <ul className="space-y-2">
                {templates.map((template) => (
                  <li key={template.key}>
                    <label className="flex cursor-pointer items-start gap-3 rounded-control border border-line bg-sunken/30 px-4 py-3 hover:bg-surface has-[:checked]:border-action/40 has-[:checked]:bg-action-soft">
                      <input
                        type="checkbox"
                        className="mt-0.5 shrink-0 accent-action"
                        checked={selected.has(template.key)}
                        onChange={() => toggle(template.key)}
                      />
                      <span className="min-w-0">
                        <span className="block text-base font-medium text-ink">{template.name}</span>
                        <span className="block text-sm text-ink-3">{template.description}</span>
                        {template.missingRoles?.length ? (
                          <span className="mt-1 block text-xs text-ink-3">
                            Also creates: {template.missingRoles.join(', ')}
                          </span>
                        ) : null}
                      </span>
                    </label>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-sm text-ink-3">
                Every template is already in this project.
              </p>
            )
          ) : templates.length ? (
            <button
              type="button"
              className="btn-ghost btn-sm"
              onClick={() => setAdding(true)}
            >
              Add another
            </button>
          ) : null}
          {apply.isError ? (
            <Notice tone="error">
              {errorText(
                apply.error,
                "Couldn't add the workflows. You can add them later in Project settings, Workflows."
              )}
            </Notice>
          ) : null}
        </div>
      )}

      <StepActions onBack={onBack}>
        {showPicker && picked.length ? (
          <>
            <button
              type="button"
              className="btn-ghost"
              onClick={workflows.length ? () => setAdding(false) : onNext}
            >
              {workflows.length ? 'Cancel' : 'Skip for now'}
            </button>
            <button
              type="button"
              className="btn-primary"
              disabled={apply.isPending}
              onClick={() => apply.mutate()}
            >
              {apply.isPending
                ? 'Adding…'
                : `Add ${picked.length} workflow${picked.length !== 1 ? 's' : ''}`}
            </button>
          </>
        ) : workflows.length ? (
          <button type="button" className="btn-primary" onClick={onNext}>
            Continue
          </button>
        ) : (
          <button type="button" className="btn-ghost" onClick={onNext}>
            Skip for now
          </button>
        )}
      </StepActions>
    </div>
  )
}

const LANE_PARTS: { name: string; text: string }[] = [
  { name: 'Inbox', text: 'New tasks land here, from you, Plane or a goblin.' },
  { name: 'Backlog', text: 'Accepted, but not next yet.' },
  {
    name: 'Ready',
    text: 'Sorted and ready to start. The top card goes first.',
  },
  {
    name: 'Workflow columns',
    text: 'One per step. Human steps and decisions get their own colour.',
  },
]

function BoardsStep({
  project,
  streams,
  loading,
  onNext,
  onBack,
}: {
  project: Project | null
  streams: StreamDefinition[]
  loading: boolean
  onNext: () => void
  onBack: () => void
}) {
  if (!project) {
    return (
      <div>
        <StepHeader title="Your boards" />
        <NeedsProject onBack={onBack} onNext={onNext} />
      </div>
    )
  }

  return (
    <div>
      <StepHeader title={`Boards for ${project.name}`}>
        <p>
          Every stream has a board. Reading left to right: the lanes where
          work waits its turn, then a column per workflow step, then Done.
          Drag cards to sort them; drag one into the first step to start it
          now.
        </p>
      </StepHeader>

      <ol className="mb-6 grid gap-2 sm:grid-cols-4">
        {LANE_PARTS.map((part) => (
          <li
            key={part.name}
            className="rounded-control border border-line bg-sunken/40 px-3 py-2.5"
          >
            <span className="block text-base font-medium text-ink">{part.name}</span>
            <span className="block text-sm text-ink-3">{part.text}</span>
          </li>
        ))}
      </ol>

      {loading ? (
        <p className="text-sm text-ink-3">Loading…</p>
      ) : streams.length === 0 ? (
        <Notice tone="signal">
          This project has no streams yet. They appear as soon as it has a
          workflow: go back a step to pick one.
        </Notice>
      ) : (
        <>
          <p className="mb-3 text-base text-ink-2">
            The two settings that matter most on day one: how many tasks run at
            once (WIP), and whether the next one starts by itself (auto-pull).
            Bug streams start with auto-pull on, the rest with it off.
          </p>
          <ul className="space-y-2">
            {streams.map((stream) => (
              <StreamRow key={stream.id} project={project} stream={stream} />
            ))}
          </ul>
          <p className="mt-3 text-sm text-ink-3">
            Lanes, ordering, budgets, the definition of ready and more are in
            Project settings, Streams.
          </p>
        </>
      )}

      <StepActions onBack={onBack}>
        <button type="button" className="btn-primary" onClick={onNext}>
          Continue
        </button>
      </StepActions>
    </div>
  )
}

/** One stream's day-one settings; every change saves straight away. */
function StreamRow({
  project,
  stream,
}: {
  project: Project
  stream: StreamDefinition
}) {
  const queryClient = useQueryClient()
  const [wip, setWip] = useState(stream.wipLimit?.toString() ?? '')
  const color = STREAM_COLOR_CLASS[stream.color]

  const save = useMutation({
    mutationFn: (body: { autoPull?: boolean; wipLimit?: number | null }) =>
      streamsApi.update(project.id, stream.key, body),
    onSuccess: () =>
      void queryClient.invalidateQueries({ queryKey: ['project', project.id] }),
  })

  function saveWip(): void {
    const value = wip.trim() === '' ? null : Math.max(1, Math.round(Number(wip)))
    if (value !== null && Number.isNaN(value)) {
      setWip(stream.wipLimit?.toString() ?? '')
      return
    }
    if (value !== stream.wipLimit) save.mutate({ wipLimit: value })
  }

  return (
    <li className="flex flex-wrap items-center gap-x-6 gap-y-3 rounded-control border border-line bg-sunken/30 px-4 py-3">
      <span className="flex min-w-0 flex-1 items-center gap-2.5">
        <span aria-hidden className={`h-2.5 w-2.5 shrink-0 rounded-full ${color.dot}`} />
        <span className="min-w-0">
          <span className="block truncate text-base font-medium text-ink">{stream.name}</span>
          <span className="block text-sm text-ink-3">
            Tickets <span className="font-mono">{stream.ticketPrefix}-1</span>, …
          </span>
        </span>
      </span>
      <label className="flex items-center gap-2 text-sm text-ink-2">
        WIP
        <input
          type="number"
          min={1}
          className="field-input w-20 py-1"
          placeholder="∞"
          value={wip}
          onChange={(event) => setWip(event.target.value)}
          onBlur={saveWip}
          aria-label={`WIP limit of ${stream.name}`}
        />
      </label>
      <label className="flex items-center gap-2 text-sm text-ink-2">
        <input
          type="checkbox"
          className="h-4 w-4 accent-[rgb(var(--action))]"
          checked={stream.autoPull}
          disabled={save.isPending}
          onChange={(event) => save.mutate({ autoPull: event.target.checked })}
        />
        Auto-pull
      </label>
      {save.isError ? (
        <span className="w-full text-sm text-danger">
          {errorText(save.error, "Couldn't save.")}
        </span>
      ) : null}
    </li>
  )
}

function AgentStep({
  workspaceId,
  agents,
  roles,
  projects,
  defaultProjectId,
  connections,
  onSaved,
  onNext,
  onBack,
}: {
  workspaceId: string
  agents: string[]
  roles: { id: string; name: string }[]
  projects: { id: string; name: string }[]
  defaultProjectId: string
  connections: ExecutorConnection[]
  onSaved: () => void
  onNext: () => void
  onBack: () => void
}) {
  const [adding, setAdding] = useState(agents.length === 0)
  const [form, setForm] = useState({
    name: 'Developer goblin',
    roleId: '',
    projectId: '',
    connectionId: '',
    model: '',
  })

  // Sensible defaults once the lists are in: the Developer role, the project
  // being set up, a connected executor.
  const defaults = useMemo(
    () => ({
      roleId:
        roles.find((r) => r.name.toLowerCase() === 'developer')?.id ??
        roles[0]?.id ??
        '',
      projectId: defaultProjectId,
      connectionId: connections[0]?.id ?? '',
    }),
    [roles, defaultProjectId, connections]
  )
  const value = {
    ...form,
    roleId: form.roleId || defaults.roleId,
    projectId: form.projectId || defaults.projectId,
    connectionId: form.connectionId || defaults.connectionId,
  }
  const connection = connections.find((c) => c.id === value.connectionId)
  const needsModel =
    connection?.kind === 'openai_compatible' && !value.model.trim()

  const create = useMutation({
    mutationFn: () =>
      agentsApi.create({
        workspaceId,
        name: value.name.trim(),
        roleId: value.roleId,
        projectId: value.projectId || undefined,
        executorId: connection?.kind ?? 'claude_cli',
        executorCredentialId: connection?.id ?? null,
        model: value.model.trim(),
      } as Parameters<typeof agentsApi.create>[0]),
    onSuccess: () => {
      setAdding(false)
      onSaved()
    },
  })

  function handleSubmit(event: FormEvent): void {
    event.preventDefault()
    create.mutate()
  }

  return (
    <div>
      <StepHeader title="Put your first goblin to work">
        <p>
          An agent is a role put to work: a job description, a brain and a
          project. When a task reaches a workflow step, an agent with that
          step's role picks it up. Start with a developer; add one for every
          other role your workflows use, such as a reviewer, whenever you like.
        </p>
      </StepHeader>

      {agents.length && !adding ? (
        <div className="space-y-3">
          <DoneNote>
            {agents.length === 1
              ? `${agents[0]} is ready to work.`
              : `You have ${agents.length} agents ready to work.`}
          </DoneNote>
          <button
            type="button"
            className="btn-ghost btn-sm"
            onClick={() => setAdding(true)}
          >
            Add another
          </button>
        </div>
      ) : (
        <form id="agent-form" className="space-y-4" onSubmit={handleSubmit}>
          <div className="flex items-center gap-4">
            <RoleAvatar
              roleName={roles.find((r) => r.id === value.roleId)?.name}
              size={56}
            />
            <Field label="Name" htmlFor="welcome-agent-name">
              <input
                id="welcome-agent-name"
                className="field-input"
                value={value.name}
                onChange={(event) =>
                  setForm({ ...form, name: event.target.value })
                }
                required
              />
            </Field>
          </div>
          <div className="grid gap-4 sm:grid-cols-[1fr_1.35fr_1fr]">
            <Field label="Role" htmlFor="welcome-agent-role">
              <select
                id="welcome-agent-role"
                className="field-input"
                value={value.roleId}
                onChange={(event) =>
                  setForm({ ...form, roleId: event.target.value })
                }
              >
                {roles.map((role) => (
                  <option key={role.id} value={role.id}>
                    {role.name}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Connection" htmlFor="welcome-agent-connection">
              <select
                id="welcome-agent-connection"
                className="field-input"
                value={value.connectionId}
                onChange={(event) =>
                  setForm({
                    ...form,
                    connectionId: event.target.value,
                    model: '',
                  })
                }
              >
                {connections.length === 0 ? (
                  <option value="">No connections yet</option>
                ) : null}
                {connections.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name} ({CONNECTION_KIND_LABEL[c.kind]})
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Project" htmlFor="welcome-agent-project">
              <select
                id="welcome-agent-project"
                className="field-input"
                value={value.projectId}
                onChange={(event) =>
                  setForm({ ...form, projectId: event.target.value })
                }
              >
                {projects.length === 0 ? (
                  <option value="">Any project</option>
                ) : null}
                {projects.map((project) => (
                  <option key={project.id} value={project.id}>
                    {project.name}
                  </option>
                ))}
              </select>
            </Field>
          </div>
          {connection ? (
            <Field label="Model" htmlFor="welcome-agent-model">
              <ModelPicker
                id="welcome-agent-model"
                workspaceId={workspaceId}
                target={{ connectionId: connection.id }}
                value={value.model}
                onChange={(model) => setForm({ ...form, model })}
                emptyLabel={
                  connection.kind === 'openai_compatible'
                    ? 'Choose a model'
                    : "The executor's default"
                }
              />
            </Field>
          ) : (
            <Notice tone="signal">
              There's no connection yet: the agent can be created, but it can
              only run once it has one. Go back a step to add it.
            </Notice>
          )}
          {create.isError ? (
            <Notice tone="error">
              {errorText(create.error, "Couldn't create the agent.")}
            </Notice>
          ) : null}
        </form>
      )}

      <StepActions onBack={onBack}>
        {adding ? (
          <>
            <button
              type="button"
              className="btn-ghost"
              onClick={agents.length ? () => setAdding(false) : onNext}
            >
              {agents.length ? 'Cancel' : 'Skip for now'}
            </button>
            <button
              type="submit"
              form="agent-form"
              className="btn-primary"
              disabled={
                create.isPending ||
                !value.name.trim() ||
                !value.roleId ||
                needsModel
              }
            >
              {create.isPending ? 'Creating…' : 'Create agent'}
            </button>
          </>
        ) : (
          <button type="button" className="btn-primary" onClick={onNext}>
            Continue
          </button>
        )}
      </StepActions>
    </div>
  )
}

/** The sidebar, group by group, with what each place is for. */
const PLACES: {
  group: string
  intro: string
  managerOnly?: boolean
  items: { name: string; text: string }[]
}[] = [
  {
    group: 'Project',
    intro: 'The top of the sidebar is the project you are in. Switch with the picker above it.',
    items: [
      { name: 'Overview', text: 'Every stream at a glance: in flight, ready, cycle time, spend this week.' },
      { name: 'Boards', text: 'One board per stream. Sort the queue, start work, follow it live.' },
      { name: 'Tasks', text: 'All tasks as a table, to search and filter.' },
      { name: 'Runs', text: 'Every goblin run, with its log, terminal and diff.' },
      { name: 'Reports', text: 'What each task produced: summaries, findings, test results.' },
      { name: 'Knowledge', text: "What the goblins know about this project's code and conventions." },
    ],
  },
  {
    group: 'Project settings',
    intro: 'Per project, in tabs.',
    managerOnly: true,
    items: [
      { name: 'Streams', text: 'Lanes, WIP, auto-pull, ordering, weekly budget, definition of ready.' },
      { name: 'Workflows', text: 'Add workflows from a template and redraw them in the editor.' },
      { name: 'Repositories', text: 'The repositories and branches goblins clone and push to.' },
      { name: 'Integrations', text: 'Plane: tasks come in from there and status goes back.' },
      { name: 'Policy', text: 'Rules, conventions, allowed websites and what needs approval.' },
      { name: 'General', text: 'Name, description and the project key.' },
    ],
  },
  {
    group: 'Work',
    intro: 'Across all projects.',
    items: [
      { name: 'Desk', text: 'Approvals, questions and human steps waiting on you.' },
      { name: 'Assistant', text: 'Chat with Goblic itself: ask, plan, or let it set things up.' },
      { name: 'All projects', text: 'Every project, plus what runs and waits right now.' },
    ],
  },
  {
    group: 'Team',
    intro: 'Who does the work, and what they know.',
    items: [
      { name: 'Agents', text: 'Your goblins: role, executor, model and project.' },
      { name: 'Roles', text: 'Job descriptions: instructions, tools, limits, quotas.' },
      { name: 'Skills', text: 'Reusable instruction packages that roles can load.' },
      { name: 'Knowledge base', text: 'Shared knowledge for the whole workspace.' },
    ],
  },
  {
    group: 'Connections',
    intro: 'What the goblins may use.',
    managerOnly: true,
    items: [
      { name: 'Executors', text: 'The logins agents think with. Add a second account any time.' },
      { name: 'MCP servers', text: 'Tool servers like Linear or a database, per role.' },
      { name: 'Credentials', text: 'GitHub and other tokens, stored encrypted.' },
    ],
  },
  {
    group: 'Workspace',
    intro: 'For you and for the record.',
    items: [
      { name: 'Audit log', text: 'Who did what, and when.' },
      { name: 'Settings', text: 'Theme, illustrations, and this welcome again.' },
    ],
  },
]

function WhereThingsLive({
  isManager,
  onNext,
  onBack,
}: {
  isManager: boolean
  onNext: () => void
  onBack: () => void
}) {
  const places = PLACES.filter((p) => isManager || !p.managerOnly)
  return (
    <div>
      <StepHeader title="Where things live">
        <p>
          The sidebar is grouped the same way. Everything you set up here can
          be changed there later.
        </p>
      </StepHeader>
      <div className="grid gap-x-8 gap-y-6 sm:grid-cols-2">
        {places.map((place) => (
          <section key={place.group}>
            <h2 className="font-display text-md font-semibold text-ink">
              {place.group}
            </h2>
            <p className="mb-2 text-sm text-ink-3">{place.intro}</p>
            <dl className="space-y-1.5">
              {place.items.map((item) => (
                <div key={item.name} className="text-sm">
                  <dt className="inline font-medium text-ink">{item.name}</dt>
                  <dd className="inline text-ink-3"> — {item.text}</dd>
                </div>
              ))}
            </dl>
          </section>
        ))}
      </div>
      <StepActions onBack={onBack}>
        <button type="button" className="btn-primary" onClick={onNext}>
          Finish
        </button>
      </StepActions>
    </div>
  )
}

function Done({
  isManager,
  ready,
  project,
  streams,
  illustrations,
}: {
  isManager: boolean
  ready: boolean
  project: Project | null
  streams: StreamDefinition[]
  illustrations: boolean
}) {
  const firstStream = streams[0]
  const canStart = isManager && ready && project != null && firstStream != null
  return (
    <div className="flex flex-col items-center py-4 text-center">
      {illustrations ? (
        <img
          src="/goblins/success.webp"
          alt=""
          aria-hidden
          width={160}
          height={160}
          className="h-40 w-40 select-none object-contain"
        />
      ) : null}
      <h1 className="mt-2 font-display text-2xl font-semibold tracking-[-0.01em] text-ink">
        {isManager && !ready ? 'Almost there' : "You're all set"}
      </h1>
      <p className="mt-2 max-w-[52ch] text-base text-ink-2">
        {!isManager
          ? 'Your projects and their boards are at the top of the menu on the left. Your Desk is where the goblins ask for you.'
          : canStart
            ? `Put a first task on the ${firstStream.name} board of ${project.name}. Move it to Ready, or start it straight away; you can follow the run live and you'll be asked before anything risky happens.`
            : "Some steps were skipped. All projects keeps a short checklist of what's left, and you can come back here from Settings."}
      </p>
      <div className="mt-6 flex flex-wrap justify-center gap-2">
        {canStart ? (
          <>
            <Link
              to="/p/$projectKey/tasks"
              params={{ projectKey: project.key }}
              search={{ new: true, stream: firstStream.key }}
              className="btn-primary"
            >
              Add a first task
            </Link>
            <Link
              to="/p/$projectKey/boards/$streamKey"
              params={{ projectKey: project.key, streamKey: firstStream.key }}
              className="btn-secondary"
            >
              Open the board
            </Link>
          </>
        ) : project ? (
          <Link
            to="/p/$projectKey/overview"
            params={{ projectKey: project.key }}
            className="btn-primary"
          >
            Go to {project.name}
          </Link>
        ) : (
          <Link to="/dashboard" className="btn-primary">
            Go to All projects
          </Link>
        )}
      </div>
      <p className="mt-6 text-sm text-ink-3">
        You can open this welcome again from Settings.
      </p>
    </div>
  )
}
